import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const Field = ({ form, set, k, label }) => (
  <div><Label className="text-xs">{label}</Label>
    <Input value={form[k] || ""} onChange={set(k)} className="mt-1 h-9" data-testid={`pd-${k}`} /></div>
);

export default function DetailsPanel({ project, reload, catalog }) {
  const [form, setForm] = useState(project);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.boundary; delete payload.layers; delete payload.prospectivity;
      delete payload.targets; delete payload.scenes; delete payload.selected_scene;
      delete payload.processing_job; delete payload.boundary_metrics; delete payload.boundary_original;
      delete payload.crs_report;
      await api.put(`/projects/${project.id}`, payload);
      toast.success("Project details saved");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field form={form} set={set} k="name" label="Project Name" />
        <Field form={form} set={set} k="project_number" label="Project Number" />
        <Field form={form} set={set} k="client_name" label="Client Name" />
        <Field form={form} set={set} k="client_company" label="Client Company" />
        <Field form={form} set={set} k="country" label="Country" />
        <Field form={form} set={set} k="region" label="Region" />
        <Field form={form} set={set} k="district" label="District" />
        <Field form={form} set={set} k="licence_number" label="Licence Number" />
        <div><Label className="text-xs">Commodity</Label>
          <Select value={form.commodity} onValueChange={set("commodity")}>
            <SelectTrigger className="mt-1 h-9" data-testid="pd-commodity"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">{(catalog?.commodities || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Field form={form} set={set} k="target_mineral" label="Target Mineral" />
        <div><Label className="text-xs">Exploration Stage</Label>
          <Select value={form.exploration_stage || ""} onValueChange={set("exploration_stage")}>
            <SelectTrigger className="mt-1 h-9" data-testid="pd-stage"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{(catalog?.exploration_stages || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Reporting Standard</Label>
          <Select value={form.reporting_standard || ""} onValueChange={set("reporting_standard")}>
            <SelectTrigger className="mt-1 h-9" data-testid="pd-standard"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{(catalog?.reporting_standards || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Status</Label>
          <Select value={form.status || "Draft"} onValueChange={set("status")}>
            <SelectTrigger className="mt-1 h-9" data-testid="pd-status"><SelectValue /></SelectTrigger>
            <SelectContent>{(catalog?.project_statuses || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Field form={form} set={set} k="report_number" label="Report Number" />
      </div>
      <div><Label className="text-xs">Description</Label>
        <Textarea value={form.description || ""} onChange={set("description")} className="mt-1" rows={3} data-testid="pd-description" /></div>
      <Button onClick={save} disabled={saving} className="w-full" data-testid="pd-save">
        {saving ? "Saving…" : "Save Project Details"}
      </Button>
    </div>
  );
}
